import { create } from "zustand";
import {
  TOKEN_KEY,
  USER_KEY,
  ACTING_TEACHER_KEY,
  ACCOUNTS_CACHE_KEY,
} from "../constants/auth";
import { v4 as uuidv4 } from "uuid";
import { setDatadogUser, clearDatadogUser } from "../utils/datadog-utils";
import { setPendoUser } from "../utils/pendo-utils";
import { setSupportSession } from "../utils/supportSession";
import { authClient } from "@/lib/auth-client";
import api, { tutorApi } from "@/utils/axios-utils";
import { clearNamePronunciationCaches } from "@/hooks/useNamePronunciationAudio";
import { isRoutableRole } from "@/utils/role-utils";
import type { ClientFlags } from "@/generated/flags";

export interface UserConfig extends Partial<ClientFlags> {
  /** Not a flag; see features/tutor/context/preferredVoice.ts. */
  preferredVoice?: {
    provider: string;
    providerVoiceId: string;
    lang: string;
    speed: string;
    updatedAtMs?: number;
  };
}

/**
 * Extended session user type from better-auth with custom properties
 */
interface SessionUser {
  id: string;
  email: string;
  name: string;
  username?: string;
  displayUsername?: string;
  account_role?: string;
  role?: string; // Legacy BetterAuth field — fallback during the account_role migration
  image?: string;
  account_type?: string;
}

export interface User {
  id: number;
  username: string;
  email?: string;
  name: string;
  displayUsername?: string; // User's preferred display username
  role: string; // Can be comma-separated roles (e.g., "STUDENT,admin")
  account_type?: string;
  picture?: string;
  config?: UserConfig;
  classrooms?: Array<{
    id: number;
    name: string;
    description: string;
  }>;
}

/**
 * Get the user's display name: displayUsername > username > name
 */
export const getDisplayName = (user: User | null | undefined): string => {
  if (!user) return "";
  return user.displayUsername || user.username || user.name || "";
};

/**
 * Single writer for the auth localStorage keys. Writes only when a value
 * actually changes, so a no-op sync never fires the cross-tab `storage` event
 * and can't drive the get-session / user_config loop between open tabs.
 */
const persistAuth = (token: string, userJson: string): void => {
  // setItem can throw (blocked site data, full quota, private windows).
  // Callers rely on this never blocking the in-memory update that follows —
  // this tab just won't survive a reload until storage is available again.
  try {
    if (token !== localStorage.getItem(TOKEN_KEY)) {
      localStorage.setItem(TOKEN_KEY, token);
    }
    if (userJson !== localStorage.getItem(USER_KEY)) {
      localStorage.setItem(USER_KEY, userJson);
    }
  } catch (error) {
    console.error("Error persisting auth to localStorage:", error);
  }
  // The support marker is deliberately NOT written here. Routing it through
  // this function looked like the safe single point, but persistAuth is also
  // called by config hydration -- which is not an auth transition and fires
  // automatically moments after syncSession establishes the marker, wiping it.
  // The marker changes only at real transitions: syncSession, setAuth,
  // clearAuth. Each states its intent explicitly below.
};

interface AuthStore {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  sessionId: string | null;
  actingTeacherId: number | null;

  setAuth: (token: string, user: User) => void;
  clearAuth: (options?: { skipServerSignOut?: boolean }) => Promise<void>;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setUserConfig: (config: UserConfig) => void;
  setActingTeacher: (teacherId: number | null) => void;
  hydrateUserConfig: () => Promise<void>;
  initialize: () => void;
  syncSession: () => Promise<void>;
}

/**
 * Read the persisted view-as target. Returns null when absent or non-numeric.
 */
const readActingTeacherId = (): number | null => {
  const raw = localStorage.getItem(ACTING_TEACHER_KEY);
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Reload to re-scope all views; redirect to /spaces if inside a specific space
 * (the current space belongs to the old teacher).
 */
const reloadAfterActingTeacherChange = (): void => {
  if (/^\/spaces\/[^/]+/.test(window.location.pathname)) {
    window.location.href = "/spaces";
  } else {
    window.location.reload();
  }
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionId: null,
  actingTeacherId: readActingTeacherId(),

  setAuth: (token, user) => {
    persistAuth(token, JSON.stringify(user));
    // Deliberately does NOT touch the support marker. setAuth is a shared
    // setter, not a login event: AccountInfoModal calls it to refresh a
    // displayed name, and Login calls it when hydrating an already-valid
    // cookie -- which may be a support cookie. Clearing here removed the
    // marker mid-session, re-enabling target-attributed telemetry and
    // restoring the unguarded Logout. The genuine credential-login paths
    // clear it themselves; see Login.tsx and hooks/useAuth.ts.
    // A fresh login is always as yourself — drop any prior view-as selection
    // and the cached accessible-accounts set (it belongs to the prior user).
    localStorage.removeItem(ACTING_TEACHER_KEY);
    localStorage.removeItem(ACCOUNTS_CACHE_KEY);

    const currentSessionId = get().sessionId;
    const sessionId = currentSessionId || uuidv4();

    // Enrich Datadog session with user info
    setDatadogUser(user);
    setPendoUser(user);

    set({
      token,
      user,
      isAuthenticated: true,
      isLoading: false,
      error: null,
      sessionId,
      actingTeacherId: null,
    });

    void get().hydrateUserConfig();
  },

  clearAuth: async (options) => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACTING_TEACHER_KEY);
    localStorage.removeItem(ACCOUNTS_CACHE_KEY);

    // Clear Datadog user on logout
    clearDatadogUser();
    setSupportSession(null);

    // Clear name-pronunciation caches so one user's data doesn't bleed into the next session.
    clearNamePronunciationCaches();

    // Sign out from the server to invalidate the session cookie
    if (!options?.skipServerSignOut) {
      try {
        await authClient.signOut();
      } catch (error) {
        console.error("Error signing out from server:", error);
      }
    }

    set({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      sessionId: null,
      actingTeacherId: null,
    });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setUserConfig: (config) => {
    const user = get().user;
    if (user) {
      const nextUser = { ...user, config };
      const nextUserJson = JSON.stringify(nextUser);
      // Only persist the token alongside the user when one actually exists in
      // state — passing "" to persistAuth would otherwise clear a valid token in
      // localStorage if state.token were ever null while state.user is set.
      const token = get().token;
      if (token) {
        persistAuth(token, nextUserJson);
      } else {
        try {
          if (nextUserJson !== localStorage.getItem(USER_KEY)) {
            localStorage.setItem(USER_KEY, nextUserJson);
          }
        } catch (error) {
          console.error("Error persisting auth to localStorage:", error);
        }
      }
      set({ user: nextUser });
    }
  },

  setActingTeacher: (teacherId) => {
    if (teacherId === null) {
      localStorage.removeItem(ACTING_TEACHER_KEY);
    } else {
      localStorage.setItem(ACTING_TEACHER_KEY, String(teacherId));
    }
    // No query layer to invalidate — a full rebuild is the reliable scope swap.
    reloadAfterActingTeacherChange();
  },

  hydrateUserConfig: async () => {
    const { isAuthenticated, user } = get();
    if (!isAuthenticated || !user) return;

    try {
      const response = await tutorApi.get<{ client_flags?: UserConfig }>(
        "/session/user_config",
      );
      const data = response.data;
      get().setUserConfig(data.client_flags || {});
    } catch (error) {
      console.error("Error hydrating user config:", error);
    }
  },

  initialize: () => {
    // Mirror acting-teacher changes made in other tabs so they don't stay stale.
    window.addEventListener("storage", (e) => {
      if (e.key !== ACTING_TEACHER_KEY) return;
      reloadAfterActingTeacherChange();
    });

    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const userStr = localStorage.getItem(USER_KEY);

      if (token && userStr) {
        const user: User = JSON.parse(userStr);

        // Enrich Datadog session with user info on page load
        setDatadogUser(user);
        setPendoUser(user);

        // If the cached role no longer routes (e.g. an account_type value like
        // "user" cached before the et-auth account_role split), keep loading so
        // route guards don't misredirect on a stale role; syncSession() heals it.
        const cachedRoleRoutable = isRoutableRole(user.role);

        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: !cachedRoleRoutable,
          error: null,
        });
        void get().hydrateUserConfig();
        // Refresh from the live session so a stale cached user (e.g. a role
        // field cached before the et-auth account_role split) self-heals.
        void get().syncSession();
      } else {
        // No local storage data, check for server session (OAuth callback)
        get().syncSession();
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: "Failed to load authentication data",
      });
    }
  },

  syncSession: async () => {
    try {
      const { data } = await authClient.getSession();
      if (data?.user) {
        const user = data.user as SessionUser;
        const parsedUserId = Number.parseInt(user.id, 10);

        if (!Number.isNaN(parsedUserId)) {
          const authUser: User = {
            id: parsedUserId,
            username: user.username || "",
            displayUsername: user.displayUsername,
            email: user.email,
            name: user.name || "",
            role: user.account_role || user.role || "STUDENT",
            account_type: user.account_type,
            picture: user.image,
            classrooms: [],
            config: get().user?.config,
          };

          const newToken = data.session?.token || "";
          // A support session authenticates as the student, so identifying
          // them here would attribute the operator's behaviour to that child
          // in Datadog, Pendo and Mixpanel. persistAuth records the marker,
          // and setDatadogUser/setPendoUser refuse on their own when it is
          // set -- which covers initialize() and every other call site.
          const supportSession = data.session as
            | { impersonatedBy?: string | null; id?: string | null }
            | undefined;
          const supportActor = supportSession?.impersonatedBy || null;
          // The session id, not the token: this is persisted in local storage
          // and the token is a credential. Same grain the backend namespaces
          // runtime sessions by, so a second grant from the same operator
          // changes the marker and other tabs actually reload.
          const supportSessionKey = supportActor
            ? supportSession?.id || null
            : null;
          persistAuth(newToken, JSON.stringify(authUser));
          // Before identifying: setDatadogUser and setPendoUser refuse on
          // their own when the marker is set.
          setSupportSession(supportActor, supportSessionKey);
          setDatadogUser(authUser);
          setPendoUser(authUser);

          set({
            token: newToken,
            user: authUser,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          void get().hydrateUserConfig();
          return;
        }
      }

      // Confirmed no session: an expired or server-revoked support session
      // never passes through clearAuth, so without this the marker survives
      // into the next ordinary login and silences that user's telemetry.
      setSupportSession(null);
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error("Error syncing session:", error);
      // Don't clobber a valid cached session on a transient fetch failure, but
      // always clear loading so an initialize() that deferred on a stale cached
      // role can't get stuck on the spinner.
      if (get().isAuthenticated) {
        set({ isLoading: false });
        return;
      }
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },
}));

useAuthStore.getState().initialize();

if (typeof window !== "undefined") {
  // Cross-tab sync must be READ-ONLY: hydrate Zustand from what the other tab
  // wrote, without any network call or localStorage write. Calling initialize()
  // here would write back (syncSession/hydrateUserConfig), fire `storage` in the
  // origin tab, and bounce a get-session / user_config loop between tabs.
  window.addEventListener("storage", (e) => {
    if (e.key !== TOKEN_KEY && e.key !== USER_KEY) return;

    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    const previousUserId = useAuthStore.getState().user?.id ?? null;

    if (!token || !userStr) {
      // Another tab logged out. If this tab was authenticated, reload so all
      // per-user state (WebSocket, session/slide stores, query caches) is torn
      // down rather than left dangling under a now-signed-out identity. A plain
      // setState would only clear the auth fields.
      if (previousUserId !== null) {
        window.location.reload();
        return;
      }
      useAuthStore.setState({
        token: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      return;
    }

    try {
      const user: User = JSON.parse(userStr);

      // Another tab switched to a DIFFERENT account. Identity-bound state in
      // this tab (live lesson WebSocket, session stores, cached data) belongs to
      // the old user, so a silent state swap would leave a mixed-identity tab.
      // Reload to rebuild the whole tab cleanly under the new user.
      if (previousUserId !== null && previousUserId !== user.id) {
        window.location.reload();
        return;
      }

      // Same user (token rotation, role self-heal, config/flags change): a
      // read-only state hydrate is enough — no reload, no localStorage write.
      // Keep monitoring/analytics enrichment consistent with the initialize()
      // and syncSession() paths, which both enrich on user change.
      setDatadogUser(user);
      setPendoUser(user);
      useAuthStore.setState({
        token,
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch {
      // Corrupt cache written by another tab — ignore and keep current state.
    }
  });
}
